// src/components/ModalUploadCertificado.jsx
import React, { useState } from "react";
import { auth } from "../firebaseConfig";
import { FaUpload, FaLock, FaSpinner } from "react-icons/fa";

const BACKEND_BASE = (process.env.REACT_APP_BACKEND_URL || "http://localhost:3000").replace(/\/+$/, "");
const apiUrl = (path) => (BACKEND_BASE ? `${BACKEND_BASE}${path}` : path);

const ModalUploadCertificado = ({ isOpen, onClose, empresaId, onUploadSuccess }) => {
  const [file, setFile] = useState(null);
  const [senha, setSenha] = useState("");
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState({ tipo: "", texto: "" });

  if (!isOpen) return null;

  const closeOnBackdrop = (e) => {
    // Não fecha enquanto estiver carregando
    if (loading) return;
    if (e.target === e.currentTarget) onClose?.();
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setMensagem({ tipo: "", texto: "" });

    if (!file || !senha) {
      setMensagem({ tipo: "erro", texto: "Escolha o arquivo .pfx e informe a senha." });
      return;
    }

    try {
      setLoading(true);

      const token = await auth.currentUser.getIdToken(true);
      const formData = new FormData();
      formData.append("certificado", file);
      formData.append("senha", senha);

      const res = await fetch(apiUrl(`/api/focus/upload-certificado/${empresaId}`), {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.mensagem || "Falha ao enviar o certificado.");

      setMensagem({ tipo: "ok", texto: "Certificado enviado com sucesso!" });
      onUploadSuccess?.();
      setTimeout(onClose, 1000);
    } catch (err) {
      console.error(err);
      setMensagem({ tipo: "erro", texto: err.message || "Erro ao enviar certificado." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-[2px] animate-fadein"
      onClick={closeOnBackdrop}
      role="dialog"
      aria-modal="true"
      aria-busy={loading}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl w-full max-w-xl relative animate-modalSlideIn overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Overlay de carregamento bloqueando tudo dentro do modal */}
        {loading && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-white/75 dark:bg-gray-900/75 backdrop-blur-sm">
            <FaSpinner className="animate-spin text-3xl mb-3 text-blue-600 dark:text-blue-400" />
            <p className="text-sm text-gray-700 dark:text-gray-200 font-medium">
              Enviando certificado… Isso pode levar alguns minutos, aguarde.
            </p>
          </div>
        )}

        <div className="max-h-[94vh] flex flex-col">
          {/* Header */}
          <div className="p-7 pb-0 relative">
            <button
              type="button"
              className={`absolute right-8 top-7 text-gray-500 hover:text-blue-700 dark:text-gray-200 dark:hover:text-white text-base font-medium ${
                loading ? "pointer-events-none opacity-50" : ""
              }`}
              onClick={onClose}
              disabled={loading}
            >
              Fechar
            </button>
            <h2 className="text-2xl font-bold mb-6 text-blue-600 dark:text-white">
              Enviar Certificado Digital (.pfx)
            </h2>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit} className="px-7 pb-6 space-y-6 overflow-y-auto flex-1">
            {/* Arquivo */}
            <div>
              <label className="block text-sm font-semibold mb-2 dark:text-gray-200">Arquivo (.pfx)</label>
              <input
                type="file"
                accept=".pfx"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="block w-full text-sm file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 border border-gray-300 dark:border-gray-700 rounded-xl px-3 py-2 bg-gray-50 dark:bg-gray-800 dark:text-white disabled:opacity-60"
                disabled={loading}
              />
              {file && (
                <p className="mt-1 text-xs text-gray-600 dark:text-gray-300 truncate">
                  Selecionado: <b>{file.name}</b>
                </p>
              )}
            </div>

            {/* Senha */}
            <div>
              <label className="block text-sm font-semibold mb-2 dark:text-gray-200">Senha do certificado</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <FaLock />
                </span>
                <input
                  type="password"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  placeholder="Digite a senha do .pfx"
                  className="border border-gray-300 dark:border-gray-700 pl-10 py-3 rounded-xl w-full bg-gray-50 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-[#004AAD] outline-none transition disabled:opacity-60"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Mensagem */}
            {mensagem.texto && (
              <p
                className={`text-sm ${
                  mensagem.tipo === "ok" ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
                }`}
                role="alert"
              >
                {mensagem.texto}
              </p>
            )}
          </form>

          {/* Footer */}
          <div className="px-7 py-5 bg-white/95 dark:bg-gray-900/95 backdrop-blur border-t border-gray-200 dark:border-gray-800">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3.5 font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition disabled:opacity-60"
            >
              {loading ? (
                <>
                  <FaSpinner className="animate-spin" /> Enviando…
                </>
              ) : (
                <>
                  <FaUpload /> Enviar Certificado
                </>
              )}
            </button>
          </div>
        </div>

        {/* Animações */}
        <style>{`
          .animate-modalSlideIn { animation: modalSlideIn 0.35s cubic-bezier(.32,.72,.57,1) both; }
          @keyframes modalSlideIn {
            0% { opacity:0; transform: translateY(60px) scale(.97); }
            100% { opacity:1; transform: translateY(0) scale(1); }
          }
          .animate-fadein { animation: fadein 0.25s; }
          @keyframes fadein { from { opacity: 0; } to { opacity: 1; } }
        `}</style>
      </div>
    </div>
  );
};

export default ModalUploadCertificado;
